import { Form, Link, useLoaderData, type LoaderFunctionArgs } from 'react-router';
import { api, toQuery } from '@/lib/api';
import { fmtDate, fmtDateTime } from '@/lib/format';

type Row = { id: string; name: string; status?: string; kind?: string; sent: number; opened: number; clicked: number; unsubscribed: number; bounced: number; openRate: number | null };
type Overview = {
  range: { from: string; to: string };
  totals: { sent: number; queued: number; failed: number; delivered: number; opens: number; clicks: number; uniqueOpens: number; uniqueClicks: number; unsubscribed: number; bounced: number; complained: number; openRate: number | null; clickRate: number | null };
  suppressions: Record<string, number>;
  campaigns: Row[];
  flows: Row[];
  templates: Row[];
  daily: Array<{ day: string; sent: number; opened: number; clicked: number; unsubscribed: number }>;
  tests: { count: number; items: Array<{ id: string; to: string; templateId: string; templateName: string; subject: string; status: string; sandbox: boolean; sentAt: string; openCount: number; clickCount: number; error: string | null }> };
};

export async function analyticsLoader({ request }: LoaderFunctionArgs) {
  const sp = new URL(request.url).searchParams;
  const range = { from: sp.get('from') ?? undefined, to: sp.get('to') ?? undefined };
  const data = await api<Overview>(`/analytics/overview${toQuery(range)}`);
  return { range, data };
}

function Table({ title, rows, href }: { title: string; rows: Row[]; href: (id: string) => string }) {
  return (
    <div className="overflow-x-auto rounded-lg border bg-white">
      <div className="border-b px-3 py-2 text-sm font-medium">{title}</div>
      <table className="data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Sent</th>
            <th>Opened</th>
            <th>Clicked</th>
            <th>Unsub</th>
            <th>Bounced</th>
            <th>Open rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="py-4 text-center text-muted-foreground">
                Nothing sent in this range
              </td>
            </tr>
          )}
          {rows.map((r) => (
            <tr key={r.id}>
              <td>
                <Link to={href(r.id)} className="ff-link">
                  {r.name}
                </Link>
                <span className="ml-2 text-xs text-muted-foreground">{r.status ?? r.kind}</span>
              </td>
              <td>{r.sent}</td>
              <td>{r.opened}</td>
              <td>{r.clicked}</td>
              <td>{r.unsubscribed}</td>
              <td>{r.bounced}</td>
              <td>{r.openRate != null ? `${r.openRate}%` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AnalyticsPage() {
  const { range, data } = useLoaderData<typeof analyticsLoader>();
  const t = data.totals;
  const max = Math.max(1, ...data.daily.map((d) => d.sent));
  const tiles: Array<[string, string | number]> = [
    ['Sent', t.sent],
    ['Delivered', t.delivered],
    ['Unique opens', `${t.uniqueOpens}${t.openRate != null ? ` (${t.openRate}%)` : ''}`],
    ['Unique clicks', `${t.uniqueClicks}${t.clickRate != null ? ` (${t.clickRate}%)` : ''}`],
    ['Unsubscribed', t.unsubscribed],
    ['Bounced', t.bounced],
    ['Complained', t.complained],
    ['Failed', t.failed],
    ['Test sends', data.tests.count],
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl">Analytics</h1>
          <p className="text-sm text-muted-foreground">
            Email performance from Mailgun events, {fmtDate(data.range.from)} → {fmtDate(data.range.to)}. Product funnels (email click → design → upgrade) live in PostHog under the same user ids.
          </p>
        </div>
        <Form key={`${range.from}-${range.to}`} className="flex items-end gap-2 text-sm" method="get">
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">From</span>
            <input type="date" name="from" defaultValue={range.from ?? ''} className="h-9 rounded-md border px-2" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">To</span>
            <input type="date" name="to" defaultValue={range.to ?? ''} className="h-9 rounded-md border px-2" />
          </label>
          <button className="h-9 rounded-md bg-[var(--ff-primary)] px-4 text-white">Apply</button>
        </Form>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {tiles.map(([label, value]) => (
          <div key={label} className="rounded-lg border bg-white p-3">
            <div className="text-xs text-muted-foreground">{label}</div>
            <div className="text-2xl">{value}</div>
          </div>
        ))}
      </div>

      <div className="rounded-lg border bg-white p-4">
        <div className="mb-2 text-sm font-medium">Daily sends · opens · clicks</div>
        {data.daily.length === 0 ? (
          <p className="text-sm text-muted-foreground">No sends in this range.</p>
        ) : (
          <div className="flex h-40 items-end gap-1">
            {data.daily.map((d) => (
              <div key={d.day} className="group relative flex flex-1 flex-col justify-end gap-px" title={`${d.day}: ${d.sent} sent, ${d.opened} opens, ${d.clicked} clicks, ${d.unsubscribed} unsub`}>
                <div className="bg-[var(--ff-primary)]" style={{ height: `${(d.sent / max) * 100}%` }} />
                <div className="bg-sky-400" style={{ height: `${(d.opened / max) * 100}%` }} />
                <div className="bg-emerald-500" style={{ height: `${(d.clicked / max) * 100}%` }} />
              </div>
            ))}
          </div>
        )}
        <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
          <span><span className="inline-block size-2 bg-[var(--ff-primary)]" /> sent</span>
          <span><span className="inline-block size-2 bg-sky-400" /> opens</span>
          <span><span className="inline-block size-2 bg-emerald-500" /> clicks</span>
          <span className="ml-auto">Suppressions: {Object.entries(data.suppressions).map(([k, v]) => `${k} ${v}`).join(' · ') || 'none'}</span>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border bg-white">
        <div className="border-b px-3 py-2 text-sm font-medium">
          Test sends <span className="font-normal text-muted-foreground">— not counted in the metrics above; sandbox mode redirects them to SANDBOX_RECIPIENT</span>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Template</th>
              <th>To</th>
              <th>Status</th>
              <th>Opens</th>
              <th>Clicks</th>
            </tr>
          </thead>
          <tbody>
            {data.tests.items.length === 0 && (
              <tr>
                <td colSpan={6} className="py-4 text-center text-muted-foreground">
                  No test sends in this range
                </td>
              </tr>
            )}
            {data.tests.items.map((t) => (
              <tr key={t.id}>
                <td className="whitespace-nowrap text-xs">{fmtDateTime(t.sentAt)}</td>
                <td>
                  <Link to={`/templates/${t.templateId}`} className="ff-link">
                    {t.templateName}
                  </Link>
                  <div className="text-xs text-muted-foreground">{t.subject}</div>
                </td>
                <td>
                  {t.to}
                  {t.sandbox && <span className="ml-1 rounded bg-amber-100 px-1 text-xs">sandbox</span>}
                </td>
                <td>
                  {t.status}
                  {t.error && <div className="text-xs text-red-700">{t.error}</div>}
                </td>
                <td>{t.openCount}</td>
                <td>{t.clickCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Table title="Campaigns" rows={data.campaigns} href={(id) => `/campaigns/${id}`} />
      <Table title="Flows" rows={data.flows} href={(id) => `/flows/${id}`} />
      <Table title="Templates" rows={data.templates} href={(id) => `/templates/${id}`} />
    </div>
  );
}
